import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import astronautSrc from 'src/assets/images/expired-window/inactive-astronaut.svg';
import { ExpiredArrowIcon } from './expired-icons';
import { useProfile } from 'src/models';
import { subscriptionClient } from 'src/lib/services/api/subscription-api';
import { openBillingPortal } from 'src/features/subscription/subscriptions-list/create-subscription-button';
import { InterceptableAxiosRequestConfig } from 'src/api/Http/Http.types';
import { useHubSpotChat } from 'src/features/hubspot/use-hubspot-chat';
import type { ExpiredSubscriptionSummary } from 'src/lib/services/api/subscription-api/subscription-client.types';
import { PlanCards } from 'src/features/plan-cards';
import {
  currentPlanNote,
  formatExpiredMetaLine,
  PLAN_INTERVALS,
  planCardPrices,
} from './expired-window.logic';

const SUPPORT_EMAIL = 'support@becreatives.co';
// The Change plan button is hidden until Roneta says otherwise (Toma, 30 Oct, #2778). The plan
// cards still open when Reactivate answers that the plan the client left isn't sold any more:
// that client has no other way back in. Flip to true to bring the button back.
const SHOW_CHANGE_PLAN_BUTTON = false;
// This screen shows its own inline errors, never the global error toast
const noGlobalToast: InterceptableAxiosRequestConfig = { _noIntercept: true };
// After the client opens the invoice page, re-check for a while, so the window clears as soon as
// Stripe marks the subscription paid (#2781). Each check waits for the previous one to finish,
// and 10s keeps it well inside the endpoint's 12-a-minute limit even with a second tab open.
const PAID_POLL_MS = 10 * 1000;
const FIRST_CHECK_MS = 2 * 1000;
const PAID_POLL_FOR_MS = 3 * 60 * 1000;

type Failure = 'reactivate' | 'plan' | 'plans-load' | 'billing' | null;
// What the client is waiting on: Reactivate, or the price id of a plan they chose
type Pending = 'reactivate' | string | null;

const InlineError: React.FC<{ what?: string }> = ({ what = 'open the payment page' }) => (
  <p role={'alert'} className={'tw-text-sm tw-text-destructive'}>
    We couldn't {what}. Try again in a moment, or email{' '}
    <a className={'tw-underline'} href={`mailto:${SUPPORT_EMAIL}`}>
      {SUPPORT_EMAIL}
    </a>
    .
  </p>
);

// Back from Stripe Checkout, which returns with ?checkout=success. Read once and cleared from the
// address, so a reload, a bookmark or the next return_url doesn't carry it on.
const takeCheckoutReturn = (): boolean => {
  const url = new URL(window.location.href);
  if (url.searchParams.get('checkout') !== 'success') {
    return false;
  }
  url.searchParams.delete('checkout');
  window.history.replaceState(window.history.state, '', url.toString());

  return true;
};

/**
 * Change plan, also shown when the plan the client left isn't sold any more (Toma's design,
 * 29 Oct): one Monthly / Quarterly / Yearly switch over three plan cards, Scale, Pro and In House
 * Team, side by side on desktop and stacked on mobile. Never Ben, never Platform Plan. Lives
 * inside the window, so it's never a separate page the gate would send back here. A choice goes
 * through the server's change-plan endpoint, never Stripe's pricing table, so there's never a
 * second subscription.
 */
const PlanPicker: React.FC<{
  pending: Pending;
  failure: Failure;
  summary: ExpiredSubscriptionSummary | undefined;
  onChoose: (priceId: string) => void;
}> = ({ pending, failure, summary, onChoose }) => {
  const plansQuery = useQuery(
    ['expired-window-plans'],
    () => subscriptionClient.products(noGlobalToast),
    { staleTime: 5 * 60 * 1000, retry: 1 },
  );

  if (plansQuery.isLoading) {
    return <Loader2 aria-label={'Loading plans'} className={'tw-size-5 tw-animate-spin'} />;
  }
  const plans = plansQuery.data?.plans;
  // An error only when nothing at all is sold; a single empty interval says so under the switch
  const sellsAnything = PLAN_INTERVALS.some((option) => planCardPrices(plans, option.id).length);
  if (plansQuery.isError || !sellsAnything) {
    return (
      <div className={'tw-flex tw-flex-col tw-gap-2'}>
        <InlineError what={'load the plans'} />
        <button
          type={'button'}
          onClick={() => plansQuery.refetch()}
          className={'tw-self-start tw-text-sm tw-font-bold tw-text-expired-ink hover:tw-underline'}
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <PlanCards
      plans={plans}
      current={currentPlanNote(summary, plans)}
      pendingPriceId={pending === 'reactivate' ? null : pending}
      disabled={pending !== null}
      onChoose={onChoose}
      error={failure === 'plan' ? <InlineError /> : null}
    />
  );
};

/**
 * The Expired window (#2780 content, #2781 buttons): what happened, one obvious way back in,
 * and everything else quiet. Copy is shared by desktop and mobile so it can't drift (#2782).
 */
const ExpiredWindow: React.FC = () => {
  const { query: profileQuery } = useProfile({ enabled: false });
  const summaryQuery = useQuery(
    ['expired-summary'],
    () => subscriptionClient.getExpiredSummary(noGlobalToast),
    {
      staleTime: 60 * 1000,
      retry: 1,
    },
  );
  const summary = summaryQuery.data;
  const metaLine = formatExpiredMetaLine(summary);
  const { openChat } = useHubSpotChat();

  // One thing at a time: while Reactivate or a plan choice is on its way, both are disabled
  const [pending, setPending] = useState<Pending>(null);
  const [isOpeningBilling, setIsOpeningBilling] = useState(false);
  const [failure, setFailure] = useState<Failure>(null);
  const [showPlans, setShowPlans] = useState(false);
  const plansRef = useRef<HTMLDivElement>(null);
  // Bumped each time the cards are asked for, so they come back into view even when already open
  const [plansAsked, setPlansAsked] = useState(0);
  const openPlans = () => {
    setShowPlans(true);
    setPlansAsked((count) => count + 1);
  };
  // Back from Stripe Checkout the purchase may still be on its way to us, so start checking
  // straight away rather than showing the window for 20-30s until a webhook lands (Toma, #2781)
  const [pollUntil, setPollUntil] = useState<number | null>(() =>
    takeCheckoutReturn() ? Date.now() + PAID_POLL_FOR_MS : null,
  );
  const refetchProfileRef = useRef(profileQuery.refetch);
  refetchProfileRef.current = profileQuery.refetch;

  // The cards open below the fold on most screens: bring them into view, without the glide for
  // anyone who has asked for less motion
  useEffect(() => {
    if (plansAsked) {
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      plansRef.current?.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    }
  }, [plansAsked]);

  // Back from Stripe via the browser's Back button: the page can come back from the back-forward
  // cache with a spinner still running. Nothing on this screen may stay dead.
  useEffect(() => {
    const resetOnRestore = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setPending(null);
        setIsOpeningBilling(false);
      }
    };
    window.addEventListener('pageshow', resetOnRestore);

    return () => window.removeEventListener('pageshow', resetOnRestore);
  }, []);

  useEffect(() => {
    if (!pollUntil) {
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const check = async () => {
      if (cancelled || Date.now() > pollUntil) {
        return;
      }
      // Ask Stripe first (a late webhook mustn't keep a paying client out), then re-read the
      // profile: once it says normal, the gate swaps this window for the page the client wanted.
      const state = await subscriptionClient
        .refreshExpiredState(noGlobalToast)
        .then((res) => res.access_state)
        .catch(() => 'expired');
      if (!cancelled && state !== 'expired') {
        refetchProfileRef.current({ cancelRefetch: false });
      }
      if (!cancelled) {
        timer = setTimeout(check, PAID_POLL_MS);
      }
    };
    // First check almost at once, then every PAID_POLL_MS
    timer = setTimeout(check, FIRST_CHECK_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pollUntil]);

  /**
   * Reactivate and Change plan both ask the server and follow its answer. Keeps `pending` while
   * this tab leaves for Stripe; clears it when the client stays here.
   */
  const followServerAnswer = async (
    what: Exclude<Pending, null>,
    request: () => Promise<{ action: string; url: string | null }>,
  ) => {
    if (pending !== null) {
      return;
    }
    setPending(what);
    setFailure(null);
    // An unpaid invoice is paid in a new tab while this one waits and clears itself. The tab
    // has to be opened inside the click for popup blockers to allow it, so it's opened only when
    // the server says an invoice is waiting; Checkout opens in this tab, with no blank tab flash.
    const invoiceTab = summary?.open_invoice !== false ? window.open('', '_blank') : null;
    const stayHere = () => {
      invoiceTab?.close();
      setPending(null);
    };

    try {
      const { action, url } = await request();

      if (action === 'pay_invoice' && url) {
        if (invoiceTab) {
          invoiceTab.opener = null;
          invoiceTab.location.href = url;
          setPollUntil(Date.now() + PAID_POLL_FOR_MS);
          setPending(null);
          return;
        }
        // Popups blocked outright: same tab, and the client comes back to a fresh check
        window.location.href = url;
        return;
      }
      if (action === 'checkout' && url) {
        // Checkout returns to this same URL, so the client lands where they were
        invoiceTab?.close();
        window.location.href = url;
        return;
      }
      if (action === 'done') {
        // Paid on the spot with the card on file: check now and the product comes back
        stayHere();
        setPollUntil(Date.now() + PAID_POLL_FOR_MS);
        return;
      }
      if (action === 'choose_plan') {
        stayHere();
        openPlans();
        return;
      }

      // Anything else is a response we can't act on: say so rather than guess
      throw new Error('Unexpected server response');
    } catch {
      stayHere();
      setFailure(what === 'reactivate' ? 'reactivate' : 'plan');
    }
  };

  const handleReactivate = () =>
    followServerAnswer('reactivate', () =>
      subscriptionClient.reactivateSubscription(window.location.href, noGlobalToast),
    );

  const handleChoosePlan = (priceId: string) =>
    followServerAnswer(priceId, () =>
      subscriptionClient.changeExpiredPlan(priceId, window.location.href, noGlobalToast),
    );

  const handleBilling = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (isOpeningBilling) {
      return;
    }
    setFailure(null);
    setIsOpeningBilling(true);
    await openBillingPortal(noGlobalToast).catch(() => {
      setFailure('billing');
      setIsOpeningBilling(false);
    });
  };

  // The chat can be blocked (an ad blocker) or still loading: then the email, never a dead click
  const handleStartChat = () => {
    if (window.HubSpotConversations?.widget) {
      openChat({ widgetOpen: true });
      return;
    }
    window.location.href = `mailto:${SUPPORT_EMAIL}`;
  };

  return (
    <div className={'tw-flex tw-grow tw-flex-col tw-bg-expired-app md:tw-pt-2'}>
      <section
        className={
          'tw-flex tw-grow tw-flex-col tw-rounded-t-[20px] tw-bg-expired-panel tw-px-5 tw-pb-6 tw-pt-8 md:tw-rounded-t-none md:tw-rounded-tl-[18px] md:tw-px-12 md:tw-py-12'
        }
      >
        {/* Content on the left, the illustration on the right from lg up. On wide screens the two
            stay together as one group in the middle of the panel, not pushed to opposite edges,
            and centred between the panel's top and the help footer (Toma, 29 Oct) */}
        <div
          className={`tw-flex tw-w-full tw-items-start tw-gap-8 lg:tw-items-center lg:tw-justify-center lg:tw-gap-16 ${
            // Centred on its own; with the plan cards open, it stays at the top above them
            showPlans ? '' : 'lg:tw-my-auto'
          }`}
        >
          <div className={'tw-flex tw-w-full tw-min-w-0 tw-max-w-[560px] tw-flex-col'}>
            {/* The confirmed design (Toma, 22 Oct): the sign-up page's Plus Jakarta Sans, heavy and
              tight, breaking over three lines on desktop and mobile alike. "Inactive", not
              "expired" (Birute's revision, 29 Oct) */}
            <h1
              className={
                'tw-max-w-[11ch] tw-font-jakarta tw-text-[40px] tw-font-extrabold tw-leading-[1.02] tw-tracking-[-0.03em] tw-text-expired-ink md:tw-text-[58px]'
              }
            >
              Your subscription is inactive
            </h1>
            <p className={'tw-mt-3 tw-text-base tw-text-expired-body'}>
              Update your payment info or re-subscribe to start editing again.
            </p>

            <div className={'tw-mt-8 tw-flex tw-flex-col tw-gap-3'}>
              {/* Reactivate primary, Change plan secondary (when shown): side by side on desktop,
                stacked full width on mobile (confirmed designs, 22 Oct) */}
              <div className={'tw-flex tw-flex-col tw-gap-3 md:tw-flex-row'}>
                <button
                  type={'button'}
                  onClick={handleReactivate}
                  disabled={pending !== null}
                  aria-busy={pending === 'reactivate'}
                  className={
                    'tw-inline-flex tw-min-h-12 tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-bg-expired-accent tw-px-6 tw-text-base tw-font-bold tw-text-white tw-transition-opacity hover:tw-opacity-90 disabled:tw-opacity-70 md:tw-w-auto'
                  }
                >
                  {pending === 'reactivate' && (
                    <Loader2 aria-hidden className={'tw-size-4 tw-animate-spin'} />
                  )}
                  Reactivate subscription
                  {pending !== 'reactivate' && (
                    <ExpiredArrowIcon className={'tw-hidden tw-w-[19px] tw-shrink-0 md:tw-block'} />
                  )}
                </button>
                {SHOW_CHANGE_PLAN_BUTTON && (
                  <button
                    type={'button'}
                    onClick={() => (showPlans ? setShowPlans(false) : openPlans())}
                    disabled={pending !== null}
                    aria-expanded={showPlans}
                    className={
                      'tw-inline-flex tw-min-h-12 tw-w-full tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-expired-divider tw-bg-expired-panel tw-px-6 tw-text-base tw-font-bold tw-text-expired-ink hover:tw-border-expired-accent disabled:tw-opacity-70 md:tw-w-auto'
                    }
                  >
                    Change plan
                  </button>
                )}
              </div>
              {failure === 'reactivate' && <InlineError />}

              {metaLine && (
                <p className={'tw-text-[14.5px] tw-font-medium tw-text-expired-meta'}>{metaLine}</p>
              )}
              <p className={'tw-text-sm tw-text-expired-quiet'}>
                You'll confirm or change your card on the next step.
              </p>
            </div>

            <hr className={'tw-my-6 tw-border-expired-divider'} />

            {summary?.billing === 'app_store' ? (
              <p className={'tw-text-[15px] tw-text-expired-body'}>
                Your billing is managed through the App Store.
              </p>
            ) : (
              <div className={'tw-flex tw-flex-col tw-gap-2'}>
                <a
                  href={'#'}
                  onClick={handleBilling}
                  aria-busy={isOpeningBilling}
                  className={
                    'tw-flex tw-min-h-11 tw-items-center tw-gap-2 tw-text-[15px] tw-font-bold tw-text-expired-ink hover:tw-underline'
                  }
                >
                  Billing &amp; invoices
                  {isOpeningBilling && (
                    <Loader2 aria-hidden className={'tw-size-4 tw-animate-spin'} />
                  )}
                </a>
                {failure === 'billing' && <InlineError />}
              </div>
            )}
          </div>

          {/* The sad astronaut with its "Inactive" watermark, one vector from Toma (29 Oct, Birute's
            revision), with its text outlined so it looks the same on every OS. Transparent, so it
            sits straight on the panel. Decorative, and desktop only (#2782: none on mobile); lazy,
            so a phone that never shows it never downloads it. */}
          <img
            src={astronautSrc}
            alt={''}
            aria-hidden
            loading={'lazy'}
            className={
              'tw-hidden tw-w-[42%] tw-max-w-[520px] tw-shrink-0 tw-select-none lg:tw-block'
            }
          />
        </div>

        {/* The plan cards need the panel's full width, not the column beside the illustration */}
        {showPlans && (
          <div
            ref={plansRef}
            className={
              'tw-mx-auto tw-mt-8 tw-w-full tw-max-w-[1100px] tw-scroll-mt-6 tw-border-t tw-border-expired-divider tw-pt-7'
            }
          >
            <PlanPicker
              pending={pending}
              failure={failure}
              summary={summary}
              onChoose={handleChoosePlan}
            />
          </div>
        )}

        {/* Desktop: "Start a chat" opens the support chat, centred on the whole panel rather than
            under the left column (Toma, 27 Oct). Mobile: the support email in bold, right under
            the content rather than pinned to the bottom, so the screen feels smaller (22 Oct). */}
        <p
          className={
            'tw-mt-10 tw-w-full tw-text-center tw-text-[14.5px] tw-text-expired-quiet md:tw-mt-auto md:tw-pt-10 lg:tw-mt-0'
          }
        >
          Need help?{' '}
          <a
            className={'tw-font-bold tw-text-expired-ink md:tw-hidden'}
            href={`mailto:${SUPPORT_EMAIL}`}
          >
            {SUPPORT_EMAIL}
          </a>
          <button
            type={'button'}
            onClick={handleStartChat}
            className={'tw-hidden tw-font-bold tw-text-expired-ink hover:tw-underline md:tw-inline'}
          >
            Start a chat
          </button>
        </p>
      </section>
    </div>
  );
};

export { ExpiredWindow };
